import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Heart,
  MapPin,
  PhoneCall,
  Plus,
  Trash2,
  Edit,
  Upload,
  Loader2,
  Link as LinkIcon,
  Globe,
  Clock,
} from "lucide-react";
import * as LucideIcons from "lucide-react";

// IMPORT FIREBASE (Aggiunti per caricare le immagini online)
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  setDoc,
  onSnapshot,
  getDocs,
  writeBatch
} from "firebase/firestore";
import { signInWithEmailAndPassword } from "firebase/auth";
import { storage, db, auth } from "./firebase";

import { translations } from "./data/translations";
import { initialContentData } from "./data/initialData";

export const TOURIST_COLORS = [
  { label: "Rosso Cotto", value: "#A64B2A" },
  { label: "Marrone Antico", value: "#5D4037" },
  { label: "Verde Mediterraneo", value: "#00695C" },
  { label: "Arancio Tramonto", value: "#BF360C" },
  { label: "Prugna Vino", value: "#880E4F" },
  { label: "Giallo Sole", value: "#F9A825" },
  { label: "Blu Zaffiro", value: "#283593" },
  { label: "Terracotta Chiaro", value: "#E2725B" },
  { label: "Rosso Emergenza", value: "#B71C1C" },
  { label: "Blu Cobalto", value: "#0047AB" },
  { label: "Verde Smeraldo", value: "#50C878" },
  { label: "Viola Prugna", value: "#301934" },
  { label: "Giallo Senape", value: "#FFDB58" },
  { label: "Azzurro Cielo", value: "#87CEEB" },
];

export const TOURIST_ICONS = [
  { value: "Utensils", label: "Cibo / Ristorazione (Utensils)" },
  { value: "Landmark", label: "Monumenti / Cultura (Landmark)" },
  { value: "Bed", label: "Alloggi / Hotel (Bed)" },
  { value: "Calendar", label: "Eventi / Attività (Calendar)" },
  { value: "Smile", label: "Bambini / Divertimento (Smile)" },
  { value: "ShoppingBag", label: "Shopping / Negozi (ShoppingBag)" },
  { value: "Info", label: "Informazioni Turistiche (Info)" },
  { value: "Phone", label: "Contatti ed Emergenze (Phone)" },
  { value: "Map", label: "Mappe / Punti (Map)" },
  { value: "Camera", label: "Fotografia / Panorama (Camera)" },
  { value: "Music", label: "Musica / Intrattenimento (Music)" },
  { value: "Palmtree", label: "Natura / Mare (Palmtree)" },
  { value: "Ticket", label: "Spettacoli / Biglietti (Ticket)" },
  { value: "Car", label: "Trasporti / Auto (Car)" },
  { value: "Coffee", label: "Bar / Caffè (Coffee)" },
  { value: "Shield", label: "Sicurezza (Shield)" },
  { value: "Heart", label: "Salute / Amore (Heart)" },
  { value: "Trophy", label: "Sport / Competizione (Trophy)" },
  { value: "Cross", label: "Ospedali / Farmacie (Cross)" },
  { value: "Wifi", label: "Connessione / Wifi (Wifi)" },
  { value: "Bus", label: "Trasporti / Bus (Bus)" },
  { value: "Siren", label: "Emergenze / SOS (Siren)" },
  { value: "FerrisWheel", label: "Ruota Panoramica / Parchi (FerrisWheel)" },
  { value: "Castle", label: "Castello (Castle)" },
  { value: "Waves", label: "Onde / Mare (Waves)" },
  { value: "Sailboat", label: "Barca a Vela (Sailboat)" },
  { value: "Anchor", label: "Ancora (Anchor)" },
  { value: "Dumbbell", label: "Palestra / Pesi (Dumbbell)" },
  { value: "Activity", label: "Attività / Cuore (Activity)" },
  { value: "TreePine", label: "Albero / Pino (TreePine)" },
  { value: "TreeDeciduous", label: "Albero (TreeDeciduous)" },
  { value: "Martini", label: "Cocktail / Bar (Martini)" },
  { value: "Wine", label: "Vino (Wine)" },
  { value: "Beer", label: "Birra (Beer)" },
  { value: "UtensilsCrossed", label: "Posate Incrociate (UtensilsCrossed)" },
  { value: "ChefHat", label: "Cappello Chef (ChefHat)" },
  { value: "Shirt", label: "Abbigliamento (Shirt)" },
  { value: "Gem", label: "Gioielleria (Gem)" },
  { value: "Crown", label: "Corona (Crown)" },
  { value: "Gift", label: "Regalo (Gift)" },
  { value: "Building", label: "Edificio / Uffici (Building)" },
  { value: "Building2", label: "Palazzo (Building2)" },
  { value: "Briefcase", label: "Valigetta (Briefcase)" },
  { value: "ShieldAlert", label: "Sicurezza Alert (ShieldAlert)" },
  { value: "SquareParking", label: "Parcheggio (SquareParking)" },
  { value: "CircleParking", label: "Parcheggio Tondo (CircleParking)" },
  { value: "Church", label: "Chiesa (Church)" },
  { value: "Library", label: "Biblioteca (Library)" },
  { value: "BookOpen", label: "Libro Aperto (BookOpen)" },
  { value: "Pickaxe", label: "Sito Archeologico / Scavi (Pickaxe)" },
];

export interface Subcategory {
  name: string;
  icon: string;
  image?: string;
}

export interface DbCategory {
  id: string;
  title: string;
  icon: string;
  color: string;
  subcategories: (string | Subcategory)[];
}

export const DynamicIcon = React.memo(({ name, size = 32 }: { name: string; size?: number }) => {
  const IconComponent = (LucideIcons as any)[name];
  if (!IconComponent) return <LucideIcons.HelpCircle size={size} />;
  return <IconComponent size={size} />;
});

// --- FUNZIONE HELPER PER LE TRADUZIONI DEI DATI ---
const normalizeString = (str: string) => str.trim().toLowerCase().replace(/\s+/g, ' ');

const getLocalizedText = (content: any, language: string) => {
  if (!content) return "";
  if (typeof content === "string") {
    const t = translations[language] || translations["it"];
    const trimmed = content.trim();
    if (t[trimmed]) return t[trimmed];
    
    // Normalization fallback
    const normContent = normalizeString(trimmed);
    const key = Object.keys(t).find(k => normalizeString(k) === normContent);
    if (key && t[key]) return t[key];

    return content;
  }
  return content[language] || content["it"] || "";
};


// --- 3. COMPONENTI UI OTTIMIZZATI ---

const Header = React.memo(
  ({ language, onLanguageChange, onHeartClick, favoritesCount, onSecretTrigger }: any) => {
    const [showLanguages, setShowLanguages] = useState(false);
    const clickTimestampsRef = useRef<number[]>([]);

    const handleTitleClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        const now = Date.now();
        const updated = [...clickTimestampsRef.current, now].filter(
          (t) => now - t <= 2000,
        );
        clickTimestampsRef.current = updated;
        if (updated.length >= 5) {
          if (onSecretTrigger) onSecretTrigger();
          clickTimestampsRef.current = [];
        }
      },
      [onSecretTrigger],
    );

    const languages = useMemo(
      () => [
        { code: "it", name: "Italiano" },
        { code: "en", name: "English" },
        { code: "de", name: "Deutsch" },
        { code: "es", name: "Español" },
      ],
      [],
    );

    return (
      <div
        style={{
          position: "fixed",
          top: "max(20px, calc(env(safe-area-inset-top) + 16px))",
          left: "max(20px, calc(env(safe-area-inset-left) + 16px))",
          right: "max(20px, calc(env(safe-area-inset-right) + 16px))",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          zIndex: 100,
          pointerEvents: "none",
        }}
      >
        <h1
          onClick={handleTitleClick}
          style={{
            margin: 0,
            fontSize: "24px",
            color: "#5C3317",
            cursor: "pointer",
            userSelect: "none",
            pointerEvents: "auto",
            textShadow: "0 2px 10px rgba(255,255,255,0.8)",
          }}
        >
          Giovinazzo
        </h1>
        <div style={{ display: "flex", gap: "15px", pointerEvents: "auto" }}>
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowLanguages(!showLanguages)}
              style={{
                background: "rgba(255, 255, 255, 0.45)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.7)",
                borderRadius: "50px",
                padding: "12px 20px",
                color: "#5C3317",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                boxShadow: "0 12px 40px 0 rgba(138, 74, 40, 0.08)",
              }}
            >
              <Globe size={18} /> {language.toUpperCase()}
            </button>
            {showLanguages && (
              <div
                style={{
                  position: "absolute",
                  top: "50px",
                  right: 0,
                  background: "rgba(255, 255, 255, 0.85)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.5)",
                  borderRadius: "15px",
                  overflow: "hidden",
                  minWidth: "150px",
                  boxShadow: "0 12px 40px 0 rgba(138, 74, 40, 0.15)",
                }}
              >
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      onLanguageChange(lang.code);
                      setShowLanguages(false);
                    }}
                    style={{
                      width: "100%",
                      padding: "10px 15px",
                      border: "none",
                      background:
                        language === lang.code
                          ? "rgba(138, 74, 40, 0.15)"
                          : "transparent",
                      color: "#5C3317",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={onHeartClick}
            style={{
              background: "rgba(255, 255, 255, 0.45)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.7)",
              borderRadius: "50px",
              width: "50px",
              height: "50px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 12px 40px 0 rgba(138, 74, 40, 0.08)",
              position: "relative",
            }}
          >
            <Heart
              size={24}
              fill={favoritesCount > 0 ? "#B71C1C" : "none"}
              color={favoritesCount > 0 ? "#B71C1C" : "#5C3317"}
            />
            {favoritesCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-5px",
                  right: "-5px",
                  background: "#B71C1C",
                  color: "white",
                  fontSize: "10px",
                  fontWeight: "bold",
                  borderRadius: "50%",
                  width: "18px",
                  height: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid white",
                }}
              >
                {favoritesCount}
              </span>
            )}
          </button>
        </div>
      </div>
    );
  },
);

const ItemCard = React.memo(
  ({
    item,
    isFavorite,
    onToggleFavorite,
    isAdmin,
    onDelete,
    onEdit,
    language,
  }: any) => {
    const [animate, setAnimate] = useState(false);
    const [isImageLoading, setIsImageLoading] = useState(true);

    const handleCardClick = useCallback(() => {
      const titleForMaps = getLocalizedText(item.title, language);
      const searchQuery = encodeURIComponent(
        `${titleForMaps}, ${item.address}, Giovinazzo`,
      );
      const url =
        item.link && item.link.length > 0
          ? item.link
          : `https://www.google.com/maps/search/?api=1&query=${searchQuery}`;
      window.open(url, "_blank");
    }, [item, language]);

    const handleHeartClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        setAnimate(true);
        onToggleFavorite(item.id);
        setTimeout(() => setAnimate(false), 300);
      },
      [item.id, onToggleFavorite],
    );

    return (
      <div
        onClick={handleCardClick}
        style={{
          background: "rgba(255, 255, 255, 0.65)",
          backdropFilter: "blur(15px)",
          borderRadius: "15px",
          overflow: "hidden",
          width: "300px",
          boxShadow: "0 15px 45px rgba(138, 74, 40, 0.08)",
          transition: "transform 0.3s ease",
          position: "relative",
          border: "1px solid rgba(255, 255, 255, 0.6)",
          cursor: "pointer",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.transform = "translateY(-5px)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.transform = "translateY(0)")
        }
      >
        <button
          onClick={handleHeartClick}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            zIndex: 10,
            background: "rgba(255, 255, 255, 0.9)",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
            transform: animate ? "scale(1.3)" : "scale(1)",
            transition: "transform 0.2s",
          }}
        >
          <Heart
            size={20}
            fill={isFavorite ? "#B71C1C" : "none"}
            color={isFavorite ? "#B71C1C" : "#999"}
          />
        </button>
        {isAdmin && (
          <>
            <button
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                console.log("✏️ Cliccato modifica su item:", item);
                // Garantisce che sia il docId di Firestore sia l'id numerico siano preservati durante il passaggio
                onEdit({ ...item, id: item.id, docId: item.docId });
              }}
              style={{
                position: "absolute",
                top: "15px",
                left: "15px",
                zIndex: 10,
                background: "#E3F2FD",
                borderRadius: "50%",
                color: "#1976D2",
                width: "40px",
                height: "40px",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <Edit size={20} />
            </button>
            {item.title !== 'Cittadella della Cultura' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item.id);
                }}
                style={{
                  position: "absolute",
                  top: "15px",
                  left: "60px",
                  zIndex: 10,
                  background: "#FFEBEE",
                  borderRadius: "50%",
                  color: "#D32F2F",
                  width: "40px",
                  height: "40px",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                }}
              >
                <Trash2 size={20} />
              </button>
            )}
          </>
        )}

        {
          item.image && (
            <div style={{ height: "180px", width: "100%", overflow: "hidden", position: "relative" }}>
              {isImageLoading && (
                <div
                  className="w-full h-full bg-stone-200 animate-pulse"
                  style={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}
                />
              )}
              <img
                src={item.image}
                alt={getLocalizedText(item.title, language)}
                loading="lazy"
                decoding="async"
                onLoad={() => setIsImageLoading(false)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  opacity: isImageLoading ? 0 : 1,
                  transition: "opacity 0.3s ease",
                  position: "relative",
                  zIndex: 2
                }}
              />
            </div>
          )
        }
        < div style={{ padding: "20px" }}>
          <h3 style={{ margin: "0 0 10px 0", color: "#5C3317" }}>
            {getLocalizedText(item.title, language)}
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: "#555",
              lineHeight: "1.5",
              marginBottom: "15px",
            }}
          >
            {item.descriptions
              ? item.descriptions[language] || item.descriptions["it"]
              : getLocalizedText(item.description, language)}
          </p>
          {
            item.address && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "13px",
                  color: "#777",
                  marginBottom: "5px",
                }}
              >
                {" "}
                <MapPin size={16} /> {item.address}{" "}
              </div>
            )
          }
          {
            item.phone && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "13px",
                  color: "#777",
                }}
              >
                {" "}
                <PhoneCall size={16} /> {item.phone}{" "}
              </div>
            )
          }
          {
            item.hours && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  fontSize: "13px",
                  color: "#777",
                  marginTop: "5px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {" "}
                <Clock size={16} style={{ flexShrink: 0, marginTop: "2px" }} /> <span>{item.hours}</span>{" "}
              </div>
            )
          }
        </div >
      </div >
    );
  },
);

// Componente Modale Aggiunta/Modifica Item (CON LOGICA FIREBASE)
const AddItemModal = ({ onClose, onSave, t, initialData, currentCategory, currentSubcategory }: any) => {
  const [formData, setFormData] = useState(() => {
    if (initialData) {
      let defaultDescriptions = { it: "", en: "", es: "", de: "" };
      if (initialData.descriptions) {
        defaultDescriptions = { ...defaultDescriptions, ...initialData.descriptions };
      } else if (initialData.description) {
        if (typeof initialData.description === "string") {
          defaultDescriptions.it = initialData.description;
        } else {
          defaultDescriptions = { ...defaultDescriptions, ...initialData.description };
        }
      }

      return {
        ...initialData,
        id: initialData.id, // TIMEPSTAMP NUMERICO OBBLIGATORIO
        docId: initialData.docId, // ID DI FIRESTORE OBBLIGATORIO
        title: initialData.title || "",
        descriptions: defaultDescriptions,
        address: initialData.address || "",
        phone: initialData.phone || "",
        hours: initialData.hours || "",
        link: initialData.link || "",
        image: initialData.image || "",
        category: initialData.category || currentCategory || "",
        subcategory: initialData.subcategory || currentSubcategory || "",
      };
    }
    return {
      title: "",
      descriptions: { it: "", en: "", es: "", de: "" },
      address: "",
      phone: "",
      hours: "",
      link: "",
      image: "",
      category: currentCategory || "",
      subcategory: currentSubcategory || "",
    };
  });
  const [isConverting, setIsConverting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const convertFileAndUpload = (file: File) => {
    setIsConverting(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 600;
        const scaleSize = MAX_WIDTH / img.width;
        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scaleSize;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // CONVERTE IN BLOB E CARICA SU FIREBASE STORAGE
          canvas.toBlob(
            async (blob) => {
              if (blob) {
                try {
                  // Crea il riferimento nel bucket di Firebase
                  const imageRef = ref(storage, `places/${Date.now()}.webp`);
                  // Carica il file WebP
                  await uploadBytes(imageRef, blob);
                  // Ottieni l'URL pubblico
                  const downloadURL = await getDownloadURL(imageRef);

                  // Salva l'URL ufficiale nel form
                  setFormData((prev: any) => ({ ...prev, image: downloadURL }));
                } catch (error) {
                  console.error(
                    "Errore durante il caricamento su Firebase:",
                    error,
                  );
                  alert("Errore durante il salvataggio dell'immagine online.");
                }
              }
              setIsConverting(false);
            },
            "image/webp",
            0.6,
          );
        }
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.5)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(20px)",
          padding: "30px",
          borderRadius: "20px",
          width: "90%",
          maxWidth: "400px",
          boxShadow: "0 25px 60px rgba(138, 74, 40, 0.2)",
          maxHeight: "90vh",
          overflowY: "auto",
          border: "1px solid rgba(255,255,255,0.7)",
        }}
      >
        <h2 style={{ marginBottom: "20px", color: "#5C3317" }}>
          {initialData ? "Modifica Luogo" : t.addItem}
        </h2>
        <input
          style={inputStyle}
          placeholder={t.formTitle}
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
        <textarea
          style={{ ...inputStyle, height: "60px", ...(formData.title === 'Cittadella della Cultura' ? { backgroundColor: "#f0f0f0", opacity: 0.7, cursor: "not-allowed" } : {}) }}
          placeholder="Descrizione IT"
          value={formData.descriptions.it}
          readOnly={formData.title === 'Cittadella della Cultura'}
          onChange={(e) =>
            setFormData({
              ...formData,
              descriptions: { ...formData.descriptions, it: e.target.value },
            })
          }
        />
        <textarea
          style={{ ...inputStyle, height: "60px", ...(formData.title === 'Cittadella della Cultura' ? { backgroundColor: "#f0f0f0", opacity: 0.7, cursor: "not-allowed" } : {}) }}
          placeholder="Descrizione EN"
          value={formData.descriptions.en}
          readOnly={formData.title === 'Cittadella della Cultura'}
          onChange={(e) =>
            setFormData({
              ...formData,
              descriptions: { ...formData.descriptions, en: e.target.value },
            })
          }
        />
        <textarea
          style={{ ...inputStyle, height: "60px", ...(formData.title === 'Cittadella della Cultura' ? { backgroundColor: "#f0f0f0", opacity: 0.7, cursor: "not-allowed" } : {}) }}
          placeholder="Descrizione ES"
          value={formData.descriptions.es}
          readOnly={formData.title === 'Cittadella della Cultura'}
          onChange={(e) =>
            setFormData({
              ...formData,
              descriptions: { ...formData.descriptions, es: e.target.value },
            })
          }
        />
        <textarea
          style={{ ...inputStyle, height: "60px", ...(formData.title === 'Cittadella della Cultura' ? { backgroundColor: "#f0f0f0", opacity: 0.7, cursor: "not-allowed" } : {}) }}
          placeholder="Descrizione DE"
          value={formData.descriptions.de}
          readOnly={formData.title === 'Cittadella della Cultura'}
          onChange={(e) =>
            setFormData({
              ...formData,
              descriptions: { ...formData.descriptions, de: e.target.value },
            })
          }
        />
        <input
          style={inputStyle}
          placeholder={t.formAddr}
          value={formData.address}
          onChange={(e) =>
            setFormData({ ...formData, address: e.target.value })
          }
        />
        <input
          style={inputStyle}
          placeholder={t.formPhone}
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        />
        <textarea
          style={{ ...inputStyle, height: "60px" }}
          placeholder="Orari di apertura"
          value={formData.hours}
          onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
        />
        <div style={{ position: "relative" }}>
          <input
            style={{ ...inputStyle, paddingLeft: "35px" }}
            placeholder={t.formLink}
            value={formData.link}
            onChange={(e) => setFormData({ ...formData, link: e.target.value })}
          />
          <LinkIcon
            size={16}
            style={{
              position: "absolute",
              top: "12px",
              left: "10px",
              color: "#999",
            }}
          />
        </div>
        <div style={{ marginBottom: "15px" }}>
          <div style={{ display: "flex", gap: "5px" }}>
            <input
              style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
              placeholder={t.formImg}
              value={
                formData.image.substring(0, 30) +
                (formData.image.length > 30 ? "..." : "")
              }
              readOnly
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: "#FF9800",
                border: "none",
                borderRadius: "8px",
                color: "white",
                cursor: "pointer",
                padding: "0 10px",
              }}
            >
              <Upload size={18} />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept="image/*"
              onChange={(e) =>
                e.target.files && convertFileAndUpload(e.target.files[0])
              }
            />
          </div>
          {isConverting && (
            <div
              style={{
                fontSize: "12px",
                color: "#FF9800",
                marginTop: "5px",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <Loader2 className="animate-spin" size={12} /> Caricamento online
              in corso...
            </div>
          )}
          {formData.image && (
            <img
              src={formData.image}
              alt="Preview"
              style={{
                marginTop: "10px",
                height: "100px",
                width: "100%",
                objectFit: "cover",
                borderRadius: "10px",
              }}
            />
          )}
        </div>
        <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
          <button
            onClick={() => onSave({
              ...formData,
              category: initialData ? (initialData.category || currentCategory) : currentCategory,
              subcategory: initialData ? (initialData.subcategory || "") : currentSubcategory
            })}
            style={saveBtnStyle}
            disabled={isConverting}
          >
            {t.save}
          </button>
          <button onClick={onClose} style={cancelBtnStyle}>
            {t.cancel}
          </button>
        </div>
      </div>
    </div>
  );
};
const inputStyle = {
  width: "100%",
  padding: "10px",
  marginBottom: "10px",
  borderRadius: "8px",
  border: "1px solid #ddd",
  fontSize: "14px",
};
const saveBtnStyle = {
  flex: 1,
  padding: "12px",
  background: "#5C3317",
  color: "white",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
  boxShadow: "0 4px 15px rgba(138, 74, 40, 0.2)",
};
const cancelBtnStyle = {
  flex: 1,
  padding: "12px",
  background: "#f5f5f5",
  color: "#333",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
};

const ManageCategoriesModal = ({ onClose }: any) => {
  const [categories, setCategories] = useState<DbCategory[]>([]);
  const [editingCat, setEditingCat] = useState<Partial<DbCategory> | null>(null);
  const [subCatInput, setSubCatInput] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "categories"), (snapshot) => {
      const list: DbCategory[] = [];
      snapshot.forEach(doc => list.push({ id: doc.id, ...doc.data() } as DbCategory));
      setCategories(list);
    });
    return () => unsub();
  }, []);

  const handleSave = async () => {
    if (!editingCat || !editingCat.title || !editingCat.icon) return alert("Titolo e Icona sono obbligatori");
    try {
      const payload = {
        title: editingCat.title,
        icon: editingCat.icon,
        color: editingCat.color || "#E2725B",
        subcategories: editingCat.subcategories || []
      };
      if (editingCat.id) {
        await updateDoc(doc(db, "categories", editingCat.id), payload);
      } else {
        await addDoc(collection(db, "categories"), payload);
      }
      setEditingCat(null);
    } catch (e) {
      console.error(e);
      alert("Errore salvataggio categoria.");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Sicuro di voler eliminare questa categoria?")) return;
    await deleteDoc(doc(db, "categories", id));
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "rgba(255, 255, 255, 0.95)", backdropFilter: "blur(20px)", padding: "30px", borderRadius: "15px", width: "90%", maxWidth: "600px", maxHeight: "90vh", overflowY: "auto", border: "1px solid rgba(255,255,255,0.7)" }}>
        <h2 style={{ marginBottom: "20px", color: "#5C3317", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Gestione Categorie
          <button onClick={onClose} style={{ background: "transparent", border: "none", fontSize: "24px", cursor: "pointer", color: "#999" }}>✕</button>
        </h2>

        {editingCat ? (
          <div style={{ border: "1px solid #ddd", padding: "20px", borderRadius: "10px", marginBottom: "20px", background: "white" }}>
            <h3 style={{ marginTop: 0, color: "#333" }}> {editingCat.id ? "Modifica Categoria" : "Nuova Categoria"} </h3>
            <input style={inputStyle} placeholder="Titolo Categoria (es. Cultura)" value={editingCat.title || ""} onChange={e => setEditingCat({ ...editingCat, title: e.target.value })} />
            <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", gap: "15px" }}>
              <span style={{ fontSize: "14px", fontWeight: "bold", minWidth: "50px" }}>Icona:</span>
              <select style={{ ...inputStyle, width: "auto", marginBottom: 0, flex: 1 }} value={editingCat.icon || ""} onChange={e => setEditingCat({ ...editingCat, icon: e.target.value })}>
                <option value="" disabled>Scegli Icona...</option>
                {TOURIST_ICONS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
              </select>
              <div style={{ width: "40px", display: "flex", justifyContent: "center", color: editingCat.color || "#999" }}>
                <DynamicIcon name={editingCat.icon || "HelpCircle"} size={28} />
              </div>
            </div>
            <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", gap: "15px" }}>
              <span style={{ fontSize: "14px", fontWeight: "bold" }}>Colore:</span>
              <select style={{ ...inputStyle, width: "auto", marginBottom: 0 }} value={editingCat.color || ""} onChange={e => setEditingCat({ ...editingCat, color: e.target.value })}>
                <option value="">Scegli Colore...</option>
                {TOURIST_COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <div style={{ width: "30px", height: "30px", borderRadius: "50%", background: editingCat.color || "#ccc", border: "2px solid #ddd" }} />
            </div>
            <div>
              <p style={{ marginBottom: "5px", fontSize: "14px", fontWeight: "bold" }}>Sottocategorie:</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "10px" }}>
                {(editingCat.subcategories || []).map((sub, idx) => (
                  <div key={idx} style={{ background: "#f0f0f0", padding: "5px 10px", borderRadius: "15px", display: "flex", alignItems: "center", gap: "5px", fontSize: "13px", border: "1px solid #ccc" }}>
                    {typeof sub === 'string' ? sub : (sub as any).name}
                    <button onClick={() => setEditingCat({ ...editingCat, subcategories: editingCat.subcategories!.filter((_, i) => i !== idx) })} style={{ background: "transparent", border: "none", color: "#D32F2F", cursor: "pointer", padding: 0, fontWeight: "bold", fontSize: "14px", marginLeft: "5px" }} title="Rimuovi">✕</button>
                  </div>
                ))}
                {(!editingCat.subcategories || editingCat.subcategories.length === 0) && (
                  <span style={{ fontSize: "13px", color: "#999" }}>Nessuna sottocategoria.</span>
                )}
              </div>
              <div style={{ display: "flex", gap: "5px" }}>
                <input style={{ ...inputStyle, marginBottom: 0, flex: 1 }} placeholder="Nuova sottocategoria" value={subCatInput} onChange={e => setSubCatInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = subCatInput.trim();
                    if (val && !(editingCat.subcategories || []).some(s => (typeof s === 'string' ? s : (s as any).name) === val)) {
                      setEditingCat({ ...editingCat, subcategories: [...(editingCat.subcategories || []), val] });
                      setSubCatInput("");
                    }
                  }
                }} />
                <button onClick={() => {
                  const val = subCatInput.trim();
                  if (val && !(editingCat.subcategories || []).some(s => (typeof s === 'string' ? s : (s as any).name) === val)) {
                    setEditingCat({ ...editingCat, subcategories: [...(editingCat.subcategories || []), val] });
                    setSubCatInput("");
                  }
                }} style={{ background: "#5C3317", color: "white", border: "none", borderRadius: "8px", padding: "0 15px", cursor: "pointer", fontWeight: "bold" }}>Aggiungi</button>
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
              <button onClick={handleSave} style={saveBtnStyle}>Salva</button>
              <button onClick={() => setEditingCat(null)} style={cancelBtnStyle}>Annulla</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setEditingCat({ id: "", title: "", icon: "Utensils", color: "", subcategories: [] })} style={{ ...saveBtnStyle, marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px", justifyContent: "center", width: "100%" }}>
            <Plus size={20} /> Aggiungi Nuova Categoria
          </button>
        )}

        <div>
          {categories.map(c => (
            <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", borderBottom: "1px solid #eee" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                <div style={{ background: c.color, color: "white", padding: "8px", borderRadius: "50%", display: "flex" }}>
                  <DynamicIcon name={c.icon} size={20} />
                </div>
                <div>
                  <strong style={{ fontSize: "15px" }}>{c.title}</strong>
                  <div style={{ fontSize: "12px", color: "#777", marginTop: "4px" }}>Sub: {c.subcategories?.map((s: any) => typeof s === 'string' ? s : s.name).join(", ") || "Nessuna"}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "15px" }}>
                <button onClick={() => setEditingCat(c)} style={{ background: "none", border: "none", cursor: "pointer", color: "#1976D2" }}><Edit size={18} /></button>
                <button onClick={() => handleDelete(c.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#D32F2F" }}><Trash2 size={18} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ManageSubcategoriesModal = ({ onClose }: any) => {
  const [categories, setCategories] = useState<DbCategory[]>([]);
  const [selectedCat, setSelectedCat] = useState<DbCategory | null>(null);
  const [editingSub, setEditingSub] = useState<Subcategory | null>(null);
  const [editingSubIdx, setEditingSubIdx] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "categories"), (snapshot) => {
      const list: DbCategory[] = [];
      snapshot.forEach(doc => list.push({ id: doc.id, ...doc.data() } as DbCategory));
      setCategories(list);
      if (selectedCat) {
        const updated = list.find(c => c.id === selectedCat.id);
        if (updated) setSelectedCat(updated);
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCat?.id]);

  const handleSaveSub = async () => {
    if (!selectedCat || !editingSub || !editingSub.name) return alert("Il nome della sottocategoria è obbligatorio");
    try {
      const newSubs = [...(selectedCat.subcategories || [])];
      
      const subToSave = { ...editingSub };
      if (!subToSave.image) {
        delete subToSave.image;
      }

      if (editingSubIdx !== null && editingSubIdx >= 0) {
        newSubs[editingSubIdx] = subToSave;
      } else {
        newSubs.push(subToSave);
      }
      
      await updateDoc(doc(db, "categories", selectedCat.id), {
        subcategories: newSubs
      });
      setEditingSub(null);
      setEditingSubIdx(null);
    } catch (e) {
      console.error(e);
      alert("Errore salvataggio sottocategoria.");
    }
  };

  const handleDeleteSub = async (idx: number) => {
    if (!selectedCat || !window.confirm("Sicuro di voler eliminare questa sottocategoria?")) return;
    try {
      const newSubs = [...(selectedCat.subcategories || [])];
      newSubs.splice(idx, 1);
      await updateDoc(doc(db, "categories", selectedCat.id), {
        subcategories: newSubs
      });
    } catch (e) {
      console.error(e);
      alert("Errore eliminazione sottocategoria.");
    }
  };

  const handleFileUpload = (file: File) => {
    setIsUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const img = new Image();
          img.onload = () => {
            try {
              const canvas = document.createElement("canvas");
              const MAX_WIDTH = 600;
              const scaleSize = MAX_WIDTH / img.width;
              canvas.width = MAX_WIDTH;
              canvas.height = img.height * scaleSize;
              const ctx = canvas.getContext("2d");
              if (ctx) {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(async (blob) => {
                  if (blob) {
                    try {
                      console.log("Inizio upload su Firebase Storage...");
                      const imageRef = ref(storage, `subcategories/${Date.now()}.webp`);
                      await uploadBytes(imageRef, blob);
                      console.log("Upload completato, recupero URL...");
                      const downloadURL = await getDownloadURL(imageRef);
                      console.log("URL recuperato:", downloadURL);
                      setEditingSub(prev => prev ? { ...prev, image: downloadURL } : null);
                    } catch (error) {
                      console.error("ERRORE DURANTE UPLOAD FIREBASE STORAGE:", error);
                      alert("Errore caricamento immagine. Controlla la console per i dettagli.");
                    }
                  } else {
                    console.error("ERRORE: Impossibile creare il blob dall'immagine.");
                  }
                  setIsUploading(false);
                }, "image/webp", 0.6);
              } else {
                console.error("ERRORE: Impossibile ottenere il contesto 2D del canvas.");
                setIsUploading(false);
              }
            } catch (err) {
              console.error("ERRORE DURANTE LA CREAZIONE DEL CANVAS:", err);
              setIsUploading(false);
            }
          };
          img.onerror = () => {
             console.error("ERRORE DURANTE IL CARICAMENTO DELL'IMMAGINE: Formato non supportato o file corrotto.");
             setIsUploading(false);
          };
          img.src = e.target?.result as string;
        } catch (err) {
          console.error("ERRORE GENERALE LETTURA FILE:", err);
          setIsUploading(false);
        }
      };
      reader.onerror = () => {
         console.error("ERRORE FILEREADER:", reader.error);
         setIsUploading(false);
      }
      reader.readAsDataURL(file);
    } catch(err) {
       console.error("ERRORE GENERALE INIZIALIZZAZIONE UPLOAD:", err);
       setIsUploading(false);
    }
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "rgba(255, 255, 255, 0.95)", backdropFilter: "blur(20px)", padding: "30px", borderRadius: "15px", width: "90%", maxWidth: "600px", maxHeight: "90vh", overflowY: "auto", border: "1px solid rgba(255,255,255,0.7)" }}>
        <h2 style={{ marginBottom: "20px", color: "#5C3317", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Gestione Sottocategorie
          <button onClick={onClose} style={{ background: "transparent", border: "none", fontSize: "24px", cursor: "pointer", color: "#999" }}>✕</button>
        </h2>

        {!selectedCat ? (
          <div>
            <p style={{ marginBottom: "15px", color: "#666" }}>Seleziona una categoria per gestirne le sottocategorie:</p>
            {categories.map(c => (
              <div key={c.id} onClick={() => setSelectedCat(c)} style={{ display: "flex", alignItems: "center", gap: "15px", padding: "12px", borderBottom: "1px solid #eee", cursor: "pointer" }}>
                <div style={{ background: c.color, color: "white", padding: "8px", borderRadius: "50%", display: "flex" }}>
                  <DynamicIcon name={c.icon} size={20} />
                </div>
                <strong style={{ fontSize: "15px" }}>{c.title}</strong>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <button onClick={() => { setSelectedCat(null); setEditingSub(null); }} style={{ background: "transparent", border: "none", color: "#1976D2", cursor: "pointer", marginBottom: "15px", display: "flex", alignItems: "center", gap: "5px" }}>
              ← Torna alle categorie
            </button>
            <h3 style={{ marginTop: 0, color: selectedCat.color, display: "flex", alignItems: "center", gap: "10px" }}>
              <DynamicIcon name={selectedCat.icon} size={24} /> {selectedCat.title}
            </h3>

            {editingSub ? (
              <div style={{ border: "1px solid #ddd", padding: "20px", borderRadius: "10px", marginBottom: "20px", background: "white" }}>
                <h4 style={{ marginTop: 0 }}>{editingSubIdx !== null ? "Modifica Sottocategoria" : "Nuova Sottocategoria"}</h4>
                <input style={inputStyle} placeholder="Nome (es. Parchi Giochi)" value={editingSub.name} onChange={e => setEditingSub({ ...editingSub, name: e.target.value })} />
                
                <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", gap: "15px" }}>
                  <span style={{ fontSize: "14px", fontWeight: "bold", minWidth: "50px" }}>Icona:</span>
                  <select style={{ ...inputStyle, width: "auto", marginBottom: 0, flex: 1 }} value={editingSub.icon || ""} onChange={e => setEditingSub({ ...editingSub, icon: e.target.value })}>
                    <option value="" disabled>Scegli Icona...</option>
                    {TOURIST_ICONS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                  </select>
                  <div style={{ width: "40px", display: "flex", justifyContent: "center", color: selectedCat.color || "#999" }}>
                    <DynamicIcon name={editingSub.icon || "HelpCircle"} size={28} />
                  </div>
                </div>

                <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: "14px", fontWeight: "bold", minWidth: "50px" }}>Img:</span>
                  <input style={{ ...inputStyle, marginBottom: 0, flex: 1 }} placeholder="Nessuna immagine (opzionale)" value={editingSub.image || ""} readOnly />
                  {editingSub.image && (
                    <button onClick={() => setEditingSub({ ...editingSub, image: "" })} style={{ background: "#D32F2F", border: "none", borderRadius: "8px", color: "white", cursor: "pointer", padding: "10px", display: "flex", alignItems: "center", justifyContent: "center" }} title="Rimuovi immagine">
                      <Trash2 size={18} />
                    </button>
                  )}
                  <button onClick={() => fileInputRef.current?.click()} style={{ background: "#FF9800", border: "none", borderRadius: "8px", color: "white", cursor: "pointer", padding: "10px", display: "flex", alignItems: "center", justifyContent: "center" }} title="Carica nuova immagine">
                    <Upload size={18} />
                  </button>
                  <input type="file" ref={fileInputRef} style={{ display: "none" }} accept="image/*" onChange={(e) => e.target.files && handleFileUpload(e.target.files[0])} />
                </div>
                {isUploading && <p style={{ fontSize: "12px", color: "#FF9800" }}>Caricamento in corso...</p>}
                {editingSub.image && <img src={editingSub.image} alt="preview" style={{ width: "100%", height: "100px", objectFit: "cover", borderRadius: "8px", marginBottom: "15px" }} />}

                <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
                  <button onClick={handleSaveSub} style={saveBtnStyle} disabled={isUploading}>Salva Sottocategoria</button>
                  <button onClick={() => setEditingSub(null)} style={cancelBtnStyle}>Annulla</button>
                </div>
              </div>
            ) : (
              <button onClick={() => { setEditingSub({ name: "", icon: selectedCat.icon, image: "" }); setEditingSubIdx(null); }} style={{ ...saveBtnStyle, marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px", justifyContent: "center", width: "100%" }}>
                <Plus size={20} /> Aggiungi Sottocategoria
              </button>
            )}

            <div>
              {(selectedCat.subcategories || []).map((sub: any, idx: number) => {
                const subName = typeof sub === 'string' ? sub : sub.name;
                const subIcon = typeof sub === 'string' || !sub.icon ? selectedCat.icon : sub.icon;
                const subImg = typeof sub === 'string' ? undefined : sub.image;
                
                return (
                  <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", borderBottom: "1px solid #eee" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                      {subImg ? (
                        <div style={{ width: "36px", height: "36px", borderRadius: "50%", backgroundImage: `url(${subImg})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                      ) : (
                        <div style={{ background: selectedCat.color, color: "white", padding: "8px", borderRadius: "50%", display: "flex" }}>
                          <DynamicIcon name={subIcon} size={20} />
                        </div>
                      )}
                      <strong style={{ fontSize: "15px" }}>{subName}</strong>
                    </div>
                    <div style={{ display: "flex", gap: "15px" }}>
                      <button onClick={() => { setEditingSub({ name: subName, icon: subIcon, image: subImg }); setEditingSubIdx(idx); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#1976D2" }}><Edit size={18} /></button>
                      <button onClick={() => handleDeleteSub(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: "#D32F2F" }}><Trash2 size={18} /></button>
                    </div>
                  </div>
                );
              })}
              {(!selectedCat.subcategories || selectedCat.subcategories.length === 0) && (
                <p style={{ textAlign: "center", color: "#999", padding: "20px 0" }}>Nessuna sottocategoria presente.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- COMPONENTE HOME (MENU) ---
const Home = ({
  categoriesData,
  onCategoryClick,
  language,
  isExpanded,
  onToggleExpand,
  activeParentCategoryId,
  setActiveParentCategoryId,
}: any) => {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const t = useMemo(() => translations[language], [language]);

  const activeParentCategory = useMemo(
    () => categoriesData.find((cat: DbCategory) => cat.id === activeParentCategoryId),
    [categoriesData, activeParentCategoryId],
  );

  const itemsToShow = useMemo(() => {
    if (activeParentCategory) {
      return (activeParentCategory.subcategories || []).map((sub: any, index: number) => {
        const subLabel = typeof sub === 'string' ? sub : sub.name;
        console.log("Valore sottocategoria grezzo:", `'${subLabel}'`, "Lunghezza:", subLabel?.length);
        const subIcon = typeof sub === 'string' || !sub.icon ? activeParentCategory.icon : sub.icon;
        const subImage = typeof sub === 'string' ? undefined : sub.image;
        
        return {
          id: `${activeParentCategory.id}-sub-${index}`,
          label: getLocalizedText(subLabel, language),
          color: activeParentCategory.color,
          icon: <DynamicIcon name={subIcon} size={28} />,
          image: subImage,
          slug: subLabel,
          isSub: true,
        };
      });
    } else {
      return categoriesData.map((cat: DbCategory) => {
        console.log("Valore categoria grezzo:", `'${cat.title}'`, "Lunghezza:", cat.title?.length);
        return {
          id: cat.id,
          label: getLocalizedText(cat.title, language),
          color: cat.color,
          icon: <DynamicIcon name={cat.icon} size={32} />,
          subcategories: cat.subcategories && cat.subcategories.length > 0 ? cat.subcategories : null,
          slug: cat.title,
        };
      });
    }
  }, [activeParentCategory, categoriesData, t, language]);

  const isSmallDevice = Math.min(windowSize.width, windowSize.height) < 768;
  const isLandscape = windowSize.width > windowSize.height;
  const isMobileLandscape = isSmallDevice && isLandscape;

  const ITEM_SIZE = isMobileLandscape ? 65 : isSmallDevice ? 75 : 85;

  const CENTER_SIZE_EXPANDED = isMobileLandscape
    ? 120
    : isSmallDevice
      ? 135
      : 150;

  const CENTER_SIZE_COLLAPSED = isMobileLandscape
    ? 90
    : isSmallDevice
      ? 110
      : 120;

  const CONTAINER_BASE_SIZE = isMobileLandscape
    ? 240
    : isSmallDevice
      ? 280
      : 360;

  // Garantiamo un quadrato perfetto che non sfonda mai la viewport, considerando che i centri delle bolle
  // sono sul bordo del contenitore. Quindi le bolle escono di ITEM_SIZE / 2 da ogni lato.
  const maxSafeWidth = windowSize.width - ITEM_SIZE - 32; // 16px di margine per lato
  const maxSafeHeight = windowSize.height - ITEM_SIZE - 60; // Margine maggiore in altezza per header/footer

  const finalContainerSize = Math.max(100, Math.min(
    CONTAINER_BASE_SIZE,
    maxSafeWidth,
    maxSafeHeight
  ));

  const handleItemClick = useCallback(
    (item: any) => {
      if (item.subcategories) setActiveParentCategoryId(item.id);
      else onCategoryClick(item.slug);
    },
    [setActiveParentCategoryId, onCategoryClick],
  );

  const handleCenterClick = useCallback(() => {
    if (activeParentCategoryId) setActiveParentCategoryId(null);
    else onToggleExpand();
  }, [
    activeParentCategoryId,
    onToggleExpand,
    setActiveParentCategoryId,
  ]);

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        position: "relative",
        overflow: "hidden",
        margin: 0,
        padding: 0,
      }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600&display=swap');`}</style>
      <div
        style={{
          position: "relative",
          width: `${finalContainerSize}px`,
          height: `${finalContainerSize}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <button
          onClick={handleCenterClick}
          style={{
            position: "relative",
            zIndex: 20,
            width: isExpanded
              ? `${CENTER_SIZE_EXPANDED}px`
              : `${CENTER_SIZE_COLLAPSED}px`,
            height: isExpanded
              ? `${CENTER_SIZE_EXPANDED}px`
              : `${CENTER_SIZE_COLLAPSED}px`,
            maxWidth: "150px",
            maxHeight: "150px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.65)",
            backdropFilter: "blur(25px)",
            border: "1px solid rgba(255, 255, 255, 0.9)",
            boxShadow: "0 15px 50px 0 rgba(139, 90, 43, 0.15)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
            overflow: "hidden",
          }}
        >
          {activeParentCategory ? (
            <div
              style={{
                zIndex: 2,
                color: activeParentCategory.color,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <DynamicIcon name={activeParentCategory.icon} size={40} />
              <span
                style={{
                  fontSize: "14px",
                  marginTop: "5px",
                  fontWeight: "bold",
                }}
              >
                {t.back}
              </span>
            </div>
          ) : (
            <>
              <img
                src="/logo512.png"
                alt="Logo"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
                style={{
                  position: "absolute",
                  top: "0",
                  left: "0",
                  width: "100%",
                  height: "100%",
                  maxWidth: "150px",
                  maxHeight: "150px",
                  objectFit: "contain",
                  opacity: 0.25,
                  zIndex: 1,
                  pointerEvents: "none",
                  borderRadius: "50%",
                }}
              />
              <div
                className="shrink-0 whitespace-nowrap font-light tracking-widest font-sans"
                style={{
                  zIndex: 3,
                  marginTop: isMobileLandscape ? "0px" : "10px",
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                  filter: "drop-shadow(0 0 8px rgba(255,255,255,0.8))",
                  transform: isExpanded ? "scale(1.3)" : "scale(1)",
                  transition:
                    "transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
                }}
              >
                <span
                  style={{
                    fontSize: isMobileLandscape
                      ? "22px"
                      : isSmallDevice
                        ? "28px"
                        : "34px",
                    lineHeight: "1",
                    color: "#5C3317",
                  }}
                >
                  GIO
                </span>
                <span
                  style={{
                    fontStretch: "condensed",
                    fontSize: isMobileLandscape
                      ? "18px"
                      : isSmallDevice
                        ? "22px"
                        : "28px",
                    marginLeft: "2px",
                    lineHeight: "1",
                    background:
                      "linear-gradient(135deg, #BF360C, #5D4037, #283593, #00695C, #5C3317, #F9A825)",
                    backgroundSize: "200% auto",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  360
                </span>
              </div>
            </>
          )}
        </button>
        {itemsToShow.map((item: any, index: number) => {
          let bubbleColor = item.color;
          if (bubbleColor === "#5C3317" || !bubbleColor) {
            bubbleColor = "#E2725B";
          }
          const totalSubs = itemsToShow.length;
          let angle = (360 / totalSubs) * index - 90;
          const angleRad = (angle * Math.PI) / 180;

          // Posizionamento in percentuale rispetto al centro del parent
          const leftPercent = 50 + Math.cos(angleRad) * 50;
          const topPercent = 50 + Math.sin(angleRad) * 50;

          const isHovered = hoveredCategory === item.id;
          const scaleValue = isExpanded ? (isHovered ? 1.15 : 1) : 0;
          return (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              onMouseEnter={() => setHoveredCategory(item.id)}
              onMouseLeave={() => setHoveredCategory(null)}
              style={{
                position: "absolute",
                top: `${topPercent}%`,
                left: `${leftPercent}%`,
                width: `${ITEM_SIZE}px`,
                height: `${ITEM_SIZE}px`,
                borderRadius: "50%",
                background: item.image 
                  ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${item.image}) center/cover no-repeat`
                  : `${bubbleColor}CC`,
                backdropFilter: item.image ? "none" : "blur(20px)",
                border: "2px solid rgba(255, 255, 255, 0.8)",
                boxShadow: isHovered
                  ? "0 20px 45px 0 rgba(139, 90, 43, 0.25)"
                  : "0 12px 35px 0 rgba(139, 90, 43, 0.15)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transform: `translate(-50%, -50%) scale(${scaleValue})`,
                opacity: isExpanded ? 1 : 0,
                transition: isHovered
                  ? "transform 0.2s ease, box-shadow 0.2s ease"
                  : `all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.05}s`,
                color: "white",
                fontSize: isMobileLandscape
                  ? "10px"
                  : isSmallDevice
                    ? "11px"
                    : "12px",
                fontWeight: "bold",
                zIndex: isHovered ? 25 : 10,
                pointerEvents: isExpanded ? "auto" : "none",
                textShadow: item.image ? "0 1px 3px rgba(0,0,0,0.9)" : "0 1px 2px rgba(0,0,0,0.3)",
              }}
            >
              <div style={{ marginBottom: "2px", filter: item.image ? "drop-shadow(0 2px 4px rgba(0,0,0,0.8))" : "none" }}>{item.icon}</div>
              <span
                style={{
                  fontSize: "inherit",
                  fontWeight: "bold",
                  lineHeight: "1",
                  textAlign: "center",
                  whiteSpace: "normal",
                  maxWidth: "95%",
                  display: "block",
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
      <div
        style={{
          position: "absolute",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          textAlign: "center",
          color: "rgba(255,255,255,0.7)",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {" "}
        <p>© 2026 Pro Loco Giovinazzo APS</p>{" "}
      </div>
    </div>
  );
};

// --- MAIN APP ---
function App() {
  const [page, setPage] = useState("home");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [language, setLanguage] = useState("it");
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [contentData, setContentData] = useState(initialContentData);
  const [categoriesData, setCategoriesData] = useState<DbCategory[]>([]);
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [showManageSubcategories, setShowManageSubcategories] = useState(false);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<any>(null);
  const [activeParentCategoryId, setActiveParentCategoryId] = useState<
    string | null
  >(null);

  const t = translations[language];

  useEffect(() => {
    const unsubscribePlaces = onSnapshot(
      collection(db, "places"),
      (querySnapshot) => {
        const data: any = {};
        querySnapshot.forEach((doc) => {
          const item: any = { ...doc.data(), id: doc.id };
          const cat = item.subcategory || item.category;
          if (!data[cat]) data[cat] = [];
          data[cat].push(item);
        });
        setContentData(data);
      },
      (error) => console.error("Error with places onSnapshot listener: ", error)
    );

    const unsubscribeCategories = onSnapshot(
      collection(db, "categories"),
      (querySnapshot) => {
        const cats: DbCategory[] = [];
        querySnapshot.forEach((doc) => {
          cats.push({ id: doc.id, ...doc.data() } as DbCategory);
        });
        setCategoriesData(cats);
      },
      (error) => console.error("Error fetching categories:", error)
    );

    return () => {
      unsubscribePlaces();
      unsubscribeCategories();
    };
  }, []);

  const toggleFavorite = useCallback((id: string) => {
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((favId) => favId !== id) : [...prev, id],
    );
  }, []);

  const handleCategoryClick = useCallback((slug: string) => {
    setSelectedCategory(slug);
    setPage("category");
  }, []);
  const handleAdminLogin = async () => {
    try {
      await signInWithEmailAndPassword(auth, adminEmail, adminPassword);
      setIsAdmin(true);
      setShowAdminLogin(false);
      setAdminPassword("");
      setAdminEmail("");
    } catch (error) {
      console.error("Login fallito:", error);
      alert("Email o password errati!");
    }
  };
  const handleEditItem = useCallback((item: any) => {
    setItemToEdit(item);
    setIsAddingItem(true);
  }, []);

  const handleAddNewItem = async (formData: any) => {
    // 0. Iniettiamo forzatamente lo stato corrente per evitare documenti orfani
    let currentCategory: any = selectedCategory;
    let currentSubcategory: any = null;
    const parentCat = categoriesData.find(c => (c.subcategories || []).includes(selectedCategory));
    if (parentCat) {
      currentCategory = parentCat.title;
      currentSubcategory = selectedCategory;
    }

    const payload = {
      ...formData,
      category: currentCategory?.id || currentCategory?.name || currentCategory,
      subcategory: currentSubcategory?.id || currentSubcategory?.name || currentSubcategory || null,
    };

    const documentId = payload.id || payload.docId || (itemToEdit ? (itemToEdit.id || itemToEdit.docId) : null);

    // 1. Validazione dei dati obbligatori
    if (!payload.title || payload.title.trim() === "") {
      alert("Attenzione: Il titolo del luogo è obbligatorio. Per favore inseriscilo.");
      return;
    }
    if (!payload.category) {
      alert("Attenzione: Nessuna categoria selezionata nel form.");
      return;
    }

    try {
      if (itemToEdit) {
        if (!documentId) {
          console.error("ERRORE: Nessun documentId trovato in fase di modifica!", { payload, itemToEdit });
          alert("Errore critico: Impossibile trovare l'ID del documento su Firestore. Sincronizzazione fallita.");
          return;
        }

        const dataToUpdate = { ...payload };
        // Eliminazione esplicita del docId dai dati payload per evitare di salvarlo inavvertitamente come campo del documento Firebase
        delete dataToUpdate.docId;

        // Rimuove eventuali valori undefined (anche annidati per sicurezza) per evitare errori in Firestore
        const cleanDataToUpdate = JSON.parse(JSON.stringify(dataToUpdate));

        // setDoc con { merge: true } gestisce elegantemente i documenti non pre-esistenti in Firebase
        await setDoc(doc(db, "places", documentId.toString()), cleanDataToUpdate, { merge: true });

        // If category changed, it will just override the old selectedCategory property array until a snapshot resolves it anyway.
        // It's perfectly safe as Firestore will sync instantly.
        alert("Luogo aggiornato con successo su Firebase!");

      } else {
        const newItemId = Date.now();
        const itemData = {
          ...payload,
          id: newItemId,
        };

        // Rimuove eventuali valori undefined per evitare errori silenziosi in Firestore
        const cleanItemData = JSON.parse(JSON.stringify(itemData));

        await addDoc(collection(db, "places"), cleanItemData);
        alert("Nuovo luogo salvato con successo su Firebase!");
      }
      setIsAddingItem(false);
      setItemToEdit(null);
    } catch (e) {
      console.error("ERRORE durante il salvataggio su Firebase: ", e);
      alert("Si è verificato un errore durante il salvataggio. Controlla la console per maggiori dettagli.");
    }
  };

  const handleDeleteItem = useCallback(
    async (docId: string) => {
      if (!window.confirm(t.deleteConfirm)) return;

      try {
        await deleteDoc(doc(db, "places", docId));
        console.log("Cancellato fisicamente da Firestore con ID:", docId);
      } catch (e) {
        console.error("Errore in Firebase durante la cancellazione:", e);
      }

      // Aggiornamento locale ottimistico "ID-Centrico" richiesto
      setContentData((prev: any) => {
        const currentList = prev[selectedCategory] || [];
        const filteredList = currentList.filter(
          (p: any) => p.id !== docId
        );

        return {
          ...prev,
          [selectedCategory]: filteredList,
        };
      });
    },
    [selectedCategory, t.deleteConfirm]
  );

  const getAllItems = useCallback(() => {
    let allItems: any[] = [];
    Object.keys(contentData).forEach((key) => {
      allItems = [...allItems, ...contentData[key]];
    });
    return allItems;
  }, [contentData]);

  const categoryItems = contentData[selectedCategory] || [];
  const favoriteItems = useMemo(
    () => getAllItems().filter((item) => favorites.includes(item.id)),
    [getAllItems, favorites],
  );

  return (
    <>
      <Header
        language={language}
        onLanguageChange={setLanguage}
        onHeartClick={() => setPage("favorites")}
        favoritesCount={favorites.length}
        onSecretTrigger={() => setShowAdminLogin(true)}
      />

      {page === "home" && (
        <Home
          categoriesData={categoriesData}
          onCategoryClick={handleCategoryClick}
          language={language}
          isExpanded={isMenuExpanded}
          onToggleExpand={() => setIsMenuExpanded(!isMenuExpanded)}
          activeParentCategoryId={activeParentCategoryId}
          setActiveParentCategoryId={setActiveParentCategoryId}
        />
      )}

      {page === "category" && (
        <div
          style={{
            minHeight: "100vh",
            background: "transparent",
            color: "#5C3317",
            padding: "100px 20px",
            paddingBottom: "40px",
          }}
        >
          <button
            onClick={() => setPage("home")}
            style={{
              background: "#5C3317",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "20px",
              cursor: "pointer",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              margin: "0 auto 20px auto",
              boxShadow: "0 4px 15px rgba(139, 90, 43, 0.2)",
            }}
          >
            {" "}
            ← {t.back}{" "}
          </button>
          <h1
            style={{
              textAlign: "center",
              marginBottom: "30px",
              textTransform: "capitalize",
            }}
          >
            {" "}
            {(() => {
              const rawTitle = getLocalizedText(selectedCategory, language);
              return rawTitle.includes('_') ? rawTitle.split('_')[1] : rawTitle;
            })()}{" "}
          </h1>
          {isAdmin && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "30px",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => {
                  setItemToEdit(null);
                  setIsAddingItem(true);
                }}
                style={{
                  background: "#5C3317",
                  color: "white",
                  border: "none",
                  padding: "15px 30px",
                  borderRadius: "50px",
                  cursor: "pointer",
                  fontSize: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  boxShadow: "0 8px 25px rgba(139, 90, 43, 0.3)",
                }}
              >
                {" "}
                <Plus size={20} /> {t.addItem}{" "}
              </button>
            </div>
          )}
          {categoryItems.length === 0 ? (
            <p style={{ textAlign: "center", opacity: 0.6, fontSize: "18px" }}>
              {t.noContent}
            </p>
          ) : (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "20px",
                justifyContent: "center",
                maxWidth: "1200px",
                margin: "0 auto",
              }}
            >
              {categoryItems.map((item: any) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  isFavorite={favorites.includes(item.id)}
                  onToggleFavorite={toggleFavorite}
                  isAdmin={isAdmin}
                  onDelete={handleDeleteItem}
                  onEdit={handleEditItem}
                  language={language}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {page === "favorites" && (
        <div
          style={{
            minHeight: "100vh",
            background: "transparent",
            color: "#5C3317",
            padding: "100px 20px",
            textAlign: "center",
          }}
        >
          <button
            onClick={() => setPage("home")}
            style={{
              background: "#5C3317",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "20px",
              cursor: "pointer",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              margin: "0 auto 20px auto",
              boxShadow: "0 4px 15px rgba(139, 90, 43, 0.2)",
            }}
          >
            {" "}
            ← {t.back}{" "}
          </button>
          <h1 style={{ marginBottom: "30px" }}>{t.favoritesTitle}</h1>
          {favoriteItems.length === 0 ? (
            <div
              style={{
                background: "rgba(255, 255, 255, 0.85)",
                backdropFilter: "blur(20px)",
                borderRadius: "20px",
                padding: "40px",
                maxWidth: "600px",
                margin: "0 auto",
                boxShadow: "0 15px 45px rgba(139, 90, 43, 0.1)",
                border: "1px solid rgba(255,255,255,0.7)",
              }}
            >
              <Heart
                size={64}
                color="#ccc"
                fill="#eee"
                style={{ marginBottom: "20px" }}
              />
              <p style={{ marginTop: "20px", opacity: 0.7 }}>
                {t.favoritesEmpty}
              </p>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "20px",
                justifyContent: "center",
                maxWidth: "1200px",
                margin: "0 auto",
              }}
            >
              {favoriteItems.map((item: any) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  isFavorite={true}
                  onToggleFavorite={toggleFavorite}
                  isAdmin={false}
                  language={language}
                />
              ))}
            </div>
          )}
        </div>
      )}
      <div
        style={{
          position: "fixed",
          bottom: "max(10px, calc(env(safe-area-inset-bottom) + 16px))",
          right: "max(10px, calc(env(safe-area-inset-right) + 16px))",
          zIndex: 100,
          display: "flex",
          gap: "10px",
        }}
      >
        {isAdmin && (
          <>
            <button
              onClick={() => setShowManageCategories(true)}
              style={{
                background: "#E3F2FD",
                border: "1px solid #1976D2",
                borderRadius: "20px",
                padding: "5px 10px",
                cursor: "pointer",
                color: "#1976D2",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              Gestione Categorie
            </button>
            <button
              onClick={() => setShowManageSubcategories(true)}
              style={{
                background: "#E8F5E9",
                border: "1px solid #388E3C",
                borderRadius: "20px",
                padding: "5px 10px",
                cursor: "pointer",
                color: "#388E3C",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              Gestione Sottocategorie
            </button>
            <button
              onClick={() => setIsAdmin(false)}
              style={{
                background: "#FFEBEE",
                border: "1px solid #D32F2F",
                borderRadius: "20px",
                padding: "5px 10px",
                cursor: "pointer",
                color: "#D32F2F",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              {" "}
              Logout{" "}
            </button>
          </>
        )}
      </div>

      {showManageCategories && isAdmin && (
        <ManageCategoriesModal onClose={() => setShowManageCategories(false)} />
      )}

      {showManageSubcategories && isAdmin && (
        <ManageSubcategoriesModal onClose={() => setShowManageSubcategories(false)} />
      )}

      {showAdminLogin && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.8)",
            zIndex: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "30px",
              borderRadius: "15px",
              maxWidth: "350px",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}
          >
            <h3
              style={{
                margin: "0 0 10px 0",
                color: "#5C3317",
                textAlign: "center",
              }}
            >
              Admin Login
            </h3>
            <input
              type="email"
              placeholder="Email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                width: "100%",
                boxSizing: "border-box",
              }}
            />
            <input
              type="password"
              placeholder="Password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                width: "100%",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <button
                onClick={() => setShowAdminLogin(false)}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "#f5f5f5",
                  color: "#333",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Annulla
              </button>
              <button
                onClick={handleAdminLogin}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "#5C3317",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                Login
              </button>
            </div>
          </div>
        </div>
      )}
      {showManageCategories && (
        <ManageCategoriesModal onClose={() => setShowManageCategories(false)} />
      )}
      {isAddingItem && (() => {
        let mainCat = selectedCategory;
        let subCat = "";
        const parentCat = categoriesData.find(c => (c.subcategories || []).includes(selectedCategory));
        if (parentCat) {
          mainCat = parentCat.title;
          subCat = selectedCategory;
        }

        return (
          <AddItemModal
            onClose={() => {
              setIsAddingItem(false);
              setItemToEdit(null);
            }}
            onSave={handleAddNewItem}
            t={t}
            initialData={itemToEdit}
            currentCategory={mainCat}
            currentSubcategory={subCat}
          />
        );
      })()}
    </>
  );
}

export default App;
